import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest, resetAdminBookingsQueries } from "@/lib/queryClient";
import { useToast } from "@/hooks/useToast";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import IconButton from "@mui/material/IconButton";
import { CloseOutlined, EditOutlined, Send } from "@mui/icons-material";
import { formatCents, getStatusChipColor } from "@/lib/utils";
import { dialogActionsSx, dialogPaperPropsLabelSafe } from "@/lib/dialogStyles";
import type { BookingWithDetails, Staff } from "@shared/schema";
import { resolveOfferingName, resolveStaffDisplayName } from "@/lib/adminApi";
import { useAdminServicesCatalog } from "@/hooks/useAdminPagedCatalogs";

interface BookingDetailModalProps {
  booking: BookingWithDetails | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  staffList?: Staff[];
  onRebook?: (booking: BookingWithDetails) => void;
  /** Opens the new-booking drawer with fields prefilled (and PATCH when `editingBookingId` is set on the preset). */
  onEdit?: (booking: BookingWithDetails) => void;
}

function formatTime12(dateStr: string | Date): string {
  return new Date(dateStr).toLocaleTimeString("en-GB", { hour: "numeric", minute: "2-digit", hour12: true });
}

/** Booking reference badge: first 8 hex chars of UUID; deterministic 8-char code for other ids. */
function formatBookingRef(id: string): string {
  const compact = id.replace(/-/g, "");
  let suffix: string;
  if (/^[0-9a-fA-F]{32}$/.test(compact)) {
    suffix = compact.slice(0, 8).toUpperCase();
  } else {
    let h = 2166136261;
    for (let i = 0; i < id.length; i++) {
      h ^= id.charCodeAt(i);
      h = Math.imul(h, 16777619);
    }
    suffix = (h >>> 0).toString(16).padStart(8, "0").slice(0, 8).toUpperCase();
  }
  return `BK-${suffix}`;
}

export default function BookingDetailModal({
  booking,
  open,
  onOpenChange,
  staffList,
  onRebook,
  onEdit,
}: BookingDetailModalProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [assigningStaff, setAssigningStaff] = useState(false);
  const [staffIdValue, setStaffIdValue] = useState<string>("none");

  useEffect(() => {
    if (!open) {
      setAssigningStaff(false);
    }
  }, [open]);

  async function invalidateBookingsAndCustomers() {
    await resetAdminBookingsQueries(queryClient);
    await queryClient.invalidateQueries({ queryKey: ["/api/admin/customers"] });
  }

  const cancelMutation = useMutation({
    mutationFn: (id: string) => apiRequest("POST", `/api/admin/bookings/${id}/cancel`),
    onSuccess: async () => {
      await invalidateBookingsAndCustomers();
      toast({ title: t("toasts.bookingCancelled") });
      onOpenChange(false);
    },
  });

  const reminderMutation = useMutation({
    mutationFn: (id: string) => apiRequest("POST", `/api/admin/bookings/${id}/send-reminder`),
    onSuccess: () => {
      toast({ title: t("bookingDetailModal.reminderSent") });
    },
  });

  const completeMutation = useMutation({
    mutationFn: (id: string) => apiRequest("POST", `/api/admin/bookings/${id}/complete`),
    onSuccess: async () => {
      await invalidateBookingsAndCustomers();
      toast({ title: t("toasts.bookingCompleted") });
      onOpenChange(false);
    },
  });

  const assignStaffMutation = useMutation({
    mutationFn: ({ id, staffId }: { id: string; staffId: string | null }) =>
      apiRequest("PATCH", `/api/admin/bookings/${id}/assign-staff`, {
        staffId: staffId == null ? null : Number(staffId),
      }),
    onSuccess: async () => {
      await invalidateBookingsAndCustomers();
      toast({ title: t("toasts.staffAssigned") });
      setAssigningStaff(false);
    },
  });

  const { services: servicesCatalog } = useAdminServicesCatalog({ enabled: open && !!booking });

  if (!booking) return null;

  const b = booking;
  const offeringTitle = resolveOfferingName(b, servicesCatalog);
  const staffName = resolveStaffDisplayName(b, staffList);
  const isUnassigned = !staffName;
  const activeStaff = staffList?.filter((s) => s.active) ?? [];
  const {partySize} = (b as { partySize?: number });
  const {cancelledAt} = (b as { cancelledAt?: string });
  const {cancelledBy} = (b as { cancelledBy?: string });
  const cancelledDateStr =
    cancelledAt &&
    new Date(cancelledAt).toLocaleString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });

  const terminalStatus =
    b.status === "cancelled" || b.status === "completed" || b.status === "no_show";

  const canAct =
    !terminalStatus && (b.status === "confirmed" || b.status === "pending_payment");
  const isUpcoming = new Date(b.startAt) > new Date();
  const actionBusy =
    cancelMutation.isPending ||
    reminderMutation.isPending ||
    completeMutation.isPending ||
    assignStaffMutation.isPending;

  function handleClose() {
    onOpenChange(false);
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      scroll="paper"
      PaperProps={dialogPaperPropsLabelSafe}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 1,
          pr: 1,
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, minWidth: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <Typography variant="h6" component="span">
              {t("bookingDetailModal.title")}
            </Typography>
            <Typography
              component="span"
              variant="caption"
              sx={{ fontFamily: "monospace", bgcolor: "action.hover", px: 1, py: 0.25, borderRadius: 1 }}
              data-testid="text-booking-ref"
            >
              {formatBookingRef(b.id)}
            </Typography>
          </Box>
        </Box>
        <IconButton aria-label={t("common.close")} onClick={handleClose} size="small" edge="end">
          <CloseOutlined />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ overflow: "visible" }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center" }}>
            <Chip size="small" label={b.status} color={getStatusChipColor(b.status)} />
            {b.paymentStatus && b.paymentStatus !== "not_required" && b.paymentStatus !== "skipped" && (
              <Chip
                size="small"
                label={b.paymentStatus === "paid" ? t("common.paid") : t("common.unpaid")}
                color={b.paymentStatus === "paid" ? "success" : "default"}
              />
            )}
            {b.source === "admin" && <Chip size="small" label={t("common.manual")} variant="outlined" />}
            {partySize != null && partySize > 1 && (
              <Chip size="small" variant="outlined" label={t("bookingDetailModal.guests", { count: partySize })} />
            )}
          </Box>
          <Typography variant="h6" data-testid="text-booking-service">
            {offeringTitle ||
              (b.service ? (
                <Typography component="span" color="text.secondary" fontStyle="italic" fontWeight={400}>
                  {t("bookingDetailModal.serviceNameUnavailable")}
                </Typography>
              ) : (
                <Typography component="span" color="text.secondary" fontStyle="italic" fontWeight={400}>
                  {t("bookingDetailModal.serviceRemoved")}
                </Typography>
              ))}
          </Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2, typography: "body2" }}>
            <Box>
              <Typography color="text.secondary" variant="caption">
                {t("common.customer")}
              </Typography>
              <Typography fontWeight={600}>
                {b.customer?.firstName} {b.customer?.lastName}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {b.customer?.email}
              </Typography>
            </Box>
            <Box>
              <Typography color="text.secondary" variant="caption">
                {t("common.staff")}
              </Typography>
              {isUnassigned ? (
                <Box>
                  <Typography variant="body2" color="text.secondary" fontStyle="italic">
                    {t("bookingDetailModal.unassigned")}
                  </Typography>
                  {!assigningStaff ? (
                    <Button size="small" onClick={() => { setAssigningStaff(true); setStaffIdValue("none"); }} data-testid="button-assign-staff">
                      {t("bookingDetailModal.assignStaff")}
                    </Button>
                  ) : (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mt: 0.5 }}>
                      <FormControl size="small" sx={{ minWidth: 140 }}>
                        <Select
                          value={staffIdValue}
                          onChange={(e) => setStaffIdValue(e.target.value)}
                          displayEmpty
                        >
                          <MenuItem value="none">{t("bookingDetailModal.unassigned")}</MenuItem>
                          {activeStaff.map((s) => (
                            <MenuItem key={s.id} value={s.id}>
                              {s.displayName}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <Button
                        size="small"
                        variant="contained"
                        disabled={assignStaffMutation.isPending}
                        onClick={() =>
                          assignStaffMutation.mutate({
                            id: b.id,
                            staffId: staffIdValue === "none" ? null : staffIdValue,
                          })
                        }
                      >
                        {t("common.save")}
                      </Button>
                      <Button size="small" onClick={() => setAssigningStaff(false)}>
                        {t("common.cancel")}
                      </Button>
                    </Box>
                  )}
                </Box>
              ) : (
                <Typography fontWeight={600}>{staffName}</Typography>
              )}
            </Box>
            <Box>
              <Typography color="text.secondary" variant="caption">
                {t("common.dateAndTime")}
              </Typography>
              <Typography fontWeight={600}>
                {new Date(b.startAt).toLocaleDateString("en-GB", {
                  weekday: "short",
                  month: "short",
                  day: "numeric",
                })}
              </Typography>
              <Typography variant="caption">
                {formatTime12(b.startAt)} – {formatTime12(b.endAt)}
              </Typography>
            </Box>
            <Box>
              <Typography color="text.secondary" variant="caption">
                {t("common.price")}
              </Typography>
              <Typography fontWeight={600}>
                {formatCents(b.service?.priceCents ?? b.priceCents ?? 0, b.currency || "GBP")}
              </Typography>
              {(b.depositAmountCents ?? 0) > 0 && (
                <Typography variant="caption" color="text.secondary" display="block">
                  {t("bookingDetailModal.depositLine", {
                    amount: formatCents(b.depositAmountCents ?? 0, b.currency || "GBP"),
                  })}
                </Typography>
              )}
            </Box>
          </Box>
          {b.notes && (
            <Box>
              <Typography color="text.secondary" variant="caption">
                {t("common.notes")}
              </Typography>
              <Typography variant="body2">{b.notes}</Typography>
            </Box>
          )}
          {b.customer?.notes && (
            <Box>
              <Typography color="text.secondary" variant="caption">
                {t("bookingDetailModal.customerNotes")}
              </Typography>
              <Typography variant="body2" fontStyle="italic">
                {b.customer?.notes}
              </Typography>
            </Box>
          )}
          {b.status === "cancelled" && cancelledAt && (
            <Box
              sx={(theme) => ({
                borderRadius: 1,
                border: 1,
                borderColor: "error.light",
                bgcolor: theme.palette.mode === "light" ? "rgba(211, 47, 47, 0.08)" : "rgba(211, 47, 47, 0.2)",
                p: 1.5,
              })}
            >
              <Typography fontWeight={600} color="error">
                {t("common.cancelledState")}
              </Typography>
              <Typography variant="caption" color="error.dark">
                {cancelledBy && cancelledDateStr
                  ? t("bookingDetailModal.cancelledBy", { date: cancelledDateStr, who: cancelledBy })
                  : cancelledDateStr}
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions
        sx={{
          ...dialogActionsSx,
          flexDirection: "column",
          alignItems: "stretch",
          gap: 1.5,
        }}
      >
        {b.status === "cancelled" && onRebook && (
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              variant="outlined"
              onClick={() => {
                onRebook(b);
                onOpenChange(false);
              }}
              data-testid="button-rebook-cancelled"
            >
              {t("bookingDetailModal.rebook")}
            </Button>
          </Box>
        )}
        {canAct && (
          <>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center" }}>
              {isUpcoming && onEdit && (
                <Button
                  variant="outlined"
                  startIcon={<EditOutlined />}
                  disabled={actionBusy}
                  onClick={() => {
                    onEdit(b);
                    handleClose();
                  }}
                  data-testid="button-edit-booking"
                >
                  {t("bookingDetailModal.edit")}
                </Button>
              )}
              {isUpcoming && (
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<Send />}
                  disabled={actionBusy}
                  onClick={() => reminderMutation.mutate(b.id)}
                  data-testid="button-send-reminder"
                >
                  {reminderMutation.isPending ? t("bookingDetailModal.sendingReminder") : t("bookingDetailModal.sendReminder")}
                </Button>
              )}
              <Button
                variant="outlined"
                disabled={actionBusy}
                onClick={() => completeMutation.mutate(b.id)}
                data-testid="button-mark-completed"
              >
                {t("bookingDetailModal.complete")}
              </Button>
            </Box>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, justifyContent: "flex-end" }}>
              <Button
                color="error"
                variant="contained"
                onClick={() => cancelMutation.mutate(b.id)}
                disabled={actionBusy}
                data-testid="button-cancel-booking"
              >
                {t("bookingDetailModal.cancel")}
              </Button>
            </Box>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
